import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Map, fromJS } from 'immutable';
import CheckSheet from 'src/screen/Protected/DeviceScreen/TestingReport/CheckSheet';
import FinalizeAndSignOff from 'src/screen/Protected/DeviceScreen/TestingReport/FinalizeAndSignOff';
import FieldReportModal from 'src/screen/Protected/DeviceScreen/Commissioning/FieldReportModal';
import NotesField from 'src/screen/Protected/DeviceScreen/Commissioning/NotesField';
import { useTranslation } from 'src/i18n';
import ProjectInfoTable from 'src/screen/Protected/DeviceScreen/Commissioning/ProjectInfoTable';
import EhtInsulationAuditHighlightColors from './EhtInsulationAuditHighlightColors';
import EhtInsulationAuditFieldInspections from './EhtInsulationAuditFieldInspections';

// EHT Insulation Audit Inspection, FM-1503-NAM Rev E.
const REPORT_TYPE = 'audit-eht-insulation';
// Only §2's questions are tasks; its highlight colours are extras (see HighlightColors).
const NOTES_TYPE = 'audit-eht-insulation-notes';
const TASK_TYPES = [NOTES_TYPE];

const PROJECT_INFO_FIELDS = ['panelTag', 'unitArea'];

// Device columns copied into the form when a device is linked, if still empty.
const PREFILL_FIELDS = [
  { path: ['projectInfo', 'panelTag'], deviceKey: 'tag' },
  { path: ['projectInfo', 'unitArea'], deviceKey: 'unit' },
];

export default function EhtInsulationAuditReport (props) {
  const { t } = useTranslation();

  const tabs = useMemo(() => [
    { value: '1', label: t('ehtInsulationAudit.tabs.projectInfo') },
    { value: '2', label: t('ehtInsulationAudit.tabs.inspectionNotes') },
    { value: '3', label: t('ehtInsulationAudit.tabs.drawingComments') },
    { value: '4', label: t('ehtInsulationAudit.tabs.fieldInspections') },
    { value: '5', label: t('ehtInsulationAudit.tabs.signOff') },
  ], [t]);

  const buildEmptyResults = useCallback((tasksByType, device) => {
    const tasks = tasksByType.get(NOTES_TYPE);
    const tasksList = tasks?.toJS ? tasks.toJS() : [];
    // Raw redux rows use `task`; the getCheckSheetTasks selector renames it to `key`.
    const emptyTasks = tasksList.map((task: any) => ({ task_id: task.task ?? task.key, status: null, comment: '' }));

    return fromJS({
      info: { type: REPORT_TYPE, deviceId: props.deviceId || null },
      [NOTES_TYPE]: emptyTasks,
      extras: {
        projectInfo: {
          panelTag: device?.get('tag') || '',
          unitArea: device?.get('unit') || '',
        },
        // Codes and their meanings are fixed by the form, so only the colour is stored.
        highlightColors: { code1: '', code2: '', code3: '', code4: '' },
        drawingComments: { notes: '' },
        // The paper form prints 60 numbered rows; seed one and let the inspector add rows.
        fieldInspections: { items: [{ isometricNumber: '', deficiencyCode: '', comment: '' }] },
        // §5 on the paper form. Captured once, in the sign-off notes box on the last tab.
        inspectionComments: { notes: '' },
      },
      signOff: {},
    }) as Map<string, any>;
  }, [props.deviceId]);

  const renderTab = useCallback((tabValue, ctx) => {
    const { allResults, extras, change, readOnly, linkedDeviceTag, handleUnlinkDevice, handleDeviceSelect } = ctx;
    switch (tabValue) {
      case '1':
        return (
          <ProjectInfoTable
            fields={PROJECT_INFO_FIELDS}
            i18nPrefix='ehtInsulationAudit'
            tagField='panelTag'
            readOnly={readOnly}
            data={extras}
            onChange={change}
            linkedDeviceTag={linkedDeviceTag}
            onUnlink={handleUnlinkDevice}
            onDeviceSelect={handleDeviceSelect}
          />
        );
      case '2':
        return (
          <>
            {/* The paper form's questions are answered by the checkbox alone; anything worth
                writing down goes in Drawing or Inspection Comments. */}
            <CheckSheet
              readOnly={readOnly}
              onChange={change}
              id={NOTES_TYPE}
              title={t('ehtInsulationAudit.inspectionNotes')}
              data={allResults?.get(NOTES_TYPE)}
              checkSheetType={NOTES_TYPE}
              showComments={false}
            />
            {/* The colour legend pairs a fixed code with its meaning, so it isn't a check
                sheet: it's a three-column table over extras. */}
            <EhtInsulationAuditHighlightColors readOnly={readOnly} data={extras} onChange={change} />
          </>
        );
      case '3':
        return (
          <NotesField
            title={t('ehtInsulationAudit.drawingComments')}
            value={extras?.getIn(['drawingComments', 'notes'])}
            path={['extras', 'drawingComments', 'notes']}
            onChange={change}
            readOnly={readOnly}
            rows={16}
          />
        );
      case '4':
        return <EhtInsulationAuditFieldInspections readOnly={readOnly} data={extras} onChange={change} />;
      case '5':
        return (
          <FinalizeAndSignOff
            readOnly={readOnly}
            data={allResults?.get('signOff')}
            onChange={change}
            notesLabel={t('ehtInsulationAudit.inspectionComments')}
            notesValue={extras?.getIn(['inspectionComments', 'notes'])}
            notesPath={['extras', 'inspectionComments', 'notes']}
          />
        );
      default:
        return null;
    }
  }, [t]);

  return (
    <FieldReportModal
      reportType={REPORT_TYPE}
      taskTypes={TASK_TYPES}
      tabs={tabs}
      title={{ label: t('ehtInsulationAudit.modalTitle') }}
      prefillFields={PREFILL_FIELDS}
      requiresStatus={false}
      buildEmptyResults={buildEmptyResults}
      renderTab={renderTab}
      deviceId={props.deviceId}
      reportId={props.reportId}
      open={props.open}
      handleClose={props.handleClose}
    />
  );
}

EhtInsulationAuditReport.propTypes = {
  handleClose: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
  deviceId: PropTypes.string,
  reportId: PropTypes.string,
};
